import { Trans, useLingui } from '@lingui/react/macro'
import { Box } from 'designSystem/components/Box'
import { Button } from 'designSystem/components/Button'
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from 'designSystem/components/InputGroup'
import { useIsEmbeddedMobileLayout } from 'features/embeddedSidebar/hooks/useIsEmbeddedMobileLayout'
import { CONTENT_TABS } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/constants'
import { FavoriteSearch } from 'features/knowledgeDocumentViewer/utils/favoriteSearchesStorage'
import { SearchQueryHistoryEntry } from 'features/knowledgeDocumentViewer/utils/searchQueryHistoryStorage'
import { Search } from 'lucide-react'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { DocumentsViewerContentFilter } from 'routes/documents/-searchParams'
import styled from 'styled-components'
import { mobileInputTextStyles } from 'styles/mobileInputTextStyles'

import { SearchSuggestionsDropdown } from './SearchSuggestionsDropdown'

const SearchBarWrapper = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
  z-index: 30;
`

const FilterSelect = styled.select`
  height: 2.25rem;
  padding: 0 1.75rem 0 0.625rem;
  border: 1px solid ${(props) => props.theme.forecast.border};
  border-radius: 0.375rem;
  background: ${(props) => props.theme.forecast.defaultButton};
  color: ${(props) => props.theme.forecast.defaultText};
  font-size: 0.8125rem;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L5 5L9 1' stroke='%23888' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.5rem center;
  flex-shrink: 0;

  &:hover {
    background-color: ${(props) => props.theme.forecast.hoverButton};
  }

  &:focus-visible {
    outline: 2px solid ${(props) => props.theme.forecast.primary};
    outline-offset: -1px;
  }
  ${mobileInputTextStyles}
`

function ContentFilterSelect({
  contentFilter,
  onContentFilterChange,
}: {
  contentFilter?: string
  onContentFilterChange: (filter: DocumentsViewerContentFilter) => void
}) {
  const { i18n } = useLingui()
  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const match = CONTENT_TABS.find((tab) => tab.value === e.target.value)
    if (match) {
      onContentFilterChange(match.value)
    }
  }

  return (
    <FilterSelect value={contentFilter} onChange={handleChange}>
      {CONTENT_TABS.map((tab) => (
        <option key={tab.value} value={tab.value}>
          {i18n._(tab.label)}
        </option>
      ))}
    </FilterSelect>
  )
}

export interface SearchToolbarProps {
  searchInput: string
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onSearchKeyDown?: (e: React.KeyboardEvent) => void
  onSearchSubmit: () => void
  placeholder: string
  rightContent?: React.ReactNode
  suggestions?: SearchQueryHistoryEntry[]
  onSuggestionSelect?: (query: string) => void
  onClearHistory?: () => void
  favoriteSearches?: FavoriteSearch[]
  isSearchFavorited?: (query: string) => boolean
  onToggleSearchFavorite?: (query: string, contentFilter: string) => void
  contentFilter?: string
  onContentFilterChange?: (filter: DocumentsViewerContentFilter) => void
  variant?: 'default' | 'landing'
}

export function SearchToolbar({
  searchInput,
  onSearchChange,
  onSearchKeyDown,
  onSearchSubmit,
  placeholder,
  rightContent,
  suggestions,
  onSuggestionSelect,
  onClearHistory,
  favoriteSearches,
  isSearchFavorited,
  onToggleSearchFavorite,
  contentFilter,
  onContentFilterChange,
  variant = 'default',
}: SearchToolbarProps) {
  const { t } = useLingui()
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const isCompact = useIsEmbeddedMobileLayout() && variant === 'default'

  const pinnedCount = favoriteSearches?.length ?? 0
  const recentCount = suggestions?.length ?? 0
  const totalCount = pinnedCount + recentCount
  const hasSuggestions = totalCount > 0

  const closeDropdown = useCallback(() => {
    setIsDropdownOpen(false)
    setHighlightedIndex(-1)
  }, [])

  const handleFocus = useCallback(() => {
    if (suggestions !== undefined) {
      setIsDropdownOpen(true)
      setHighlightedIndex(-1)
    }
  }, [suggestions])

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onSearchChange(e)
      if (suggestions !== undefined) {
        setIsDropdownOpen(true)
      }
    },
    [onSearchChange, suggestions]
  )

  const handleBlur = useCallback(() => {
    setTimeout(() => {
      if (!wrapperRef.current?.contains(document.activeElement)) {
        setIsDropdownOpen(false)
        setHighlightedIndex(-1)
      }
    }, 150)
  }, [])

  const handleSelect = useCallback(
    (query: string) => {
      onSuggestionSelect?.(query)
      closeDropdown()
    },
    [closeDropdown, onSuggestionSelect]
  )

  const handleClearHistory = useCallback(() => {
    onClearHistory?.()
    closeDropdown()
  }, [closeDropdown, onClearHistory])

  const handleSearchButtonClick = useCallback(() => {
    closeDropdown()
    onSearchSubmit()
  }, [closeDropdown, onSearchSubmit])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeDropdown()
        return
      }

      if (e.key === 'Enter' && (!isDropdownOpen || !hasSuggestions)) {
        closeDropdown()
        onSearchKeyDown?.(e)
        return
      }

      if (!isDropdownOpen || !hasSuggestions) {
        onSearchKeyDown?.(e)
        return
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setHighlightedIndex((prev) => (prev + 1) % totalCount)
        return
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setHighlightedIndex((prev) => (prev <= 0 ? totalCount - 1 : prev - 1))
        return
      }

      if (e.key === 'Enter' && highlightedIndex >= 0) {
        e.preventDefault()
        e.stopPropagation()
        const pinned = favoriteSearches ?? []
        if (highlightedIndex < pinned.length) {
          handleSelect(pinned[highlightedIndex].query)
        } else {
          const recentIndex = highlightedIndex - pinned.length
          const filtered = (suggestions ?? []).filter(
            (s) =>
              !pinned.some(
                (p) => p.query.toLowerCase() === s.query.toLowerCase()
              )
          )
          if (filtered[recentIndex]) {
            handleSelect(filtered[recentIndex].query)
          }
        }
        return
      }

      if (e.key === 'Enter') {
        closeDropdown()
        onSearchKeyDown?.(e)
        return
      }

      onSearchKeyDown?.(e)
    },
    [
      closeDropdown,
      isDropdownOpen,
      hasSuggestions,
      totalCount,
      suggestions,
      favoriteSearches,
      highlightedIndex,
      handleSelect,
      onSearchKeyDown,
    ]
  )

  useEffect(() => {
    setHighlightedIndex(-1)
  }, [searchInput])

  const showDropdown =
    isDropdownOpen &&
    suggestions !== undefined &&
    onSuggestionSelect &&
    (hasSuggestions || !searchInput.trim())

  const searchBar = (
    <SearchBarWrapper ref={wrapperRef} onBlur={handleBlur}>
      <InputGroup variant='surface'>
        <InputGroupInput
          type='search'
          value={searchInput}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          placeholder={placeholder}
        />
        <InputGroupAddon align='inline-start' aria-hidden='true'>
          <Search />
        </InputGroupAddon>
      </InputGroup>
      {showDropdown && (
        <SearchSuggestionsDropdown
          suggestions={suggestions}
          favoriteSearches={favoriteSearches}
          isSearchFavorited={isSearchFavorited}
          onToggleSearchFavorite={onToggleSearchFavorite}
          contentFilter={contentFilter}
          onSelect={handleSelect}
          onClearHistory={handleClearHistory}
          highlightedIndex={highlightedIndex}
        />
      )}
    </SearchBarWrapper>
  )

  const searchButton = isCompact ? (
    <Button
      variant='primaryRaised'
      size='icon-lg'
      aria-label={t`Search`}
      title={t`Search`}
      onClick={handleSearchButtonClick}>
      <Search />
    </Button>
  ) : (
    <Button
      variant='primaryRaised'
      size={variant === 'landing' ? 'default' : 'lg'}
      onClick={handleSearchButtonClick}>
      <Search />
      <Trans>Search</Trans>
    </Button>
  )

  if (variant === 'landing') {
    return (
      <Box
        display='flex'
        align='center'
        gap='md'
        padding='sm'
        surface='muted'
        width='full'
        radius='search'>
        {searchBar}
        {searchButton}
      </Box>
    )
  }

  return (
    <Box
      display='flex'
      align='center'
      gap={isCompact ? 'sm' : 'md'}
      padding='sm'
      margin='sm'
      surface='muted'
      shrink={false}
      radius='search'>
      {searchBar}
      {searchButton}
      {onContentFilterChange && (
        <ContentFilterSelect
          contentFilter={contentFilter}
          onContentFilterChange={onContentFilterChange}
        />
      )}
      {rightContent}
    </Box>
  )
}
